import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLines } from "../lib/motion";
import { HERO_IMG, STATS } from "../lib/data";
import { LocalImage } from "./LocalImage";

export const Hero = () => {
  const ref = useRef(null);
  const reducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="hero-section relative overflow-hidden">
      <motion.div style={reducedMotion ? undefined : { y: yImg }} className="hero-visual absolute inset-0">
        <LocalImage src={HERO_IMG} alt="BMW mit geöffneter Motorhaube, Laptop und angeschlossenem Steuergerät bei ECU SOLUTIONS"
          loading="eager" fetchPriority="high" width={1152} height={2048} className="hero-image w-full h-full object-cover" />
        <div className="hero-shade absolute inset-0" />
      </motion.div>

      <motion.div style={reducedMotion ? undefined : { y: yText, opacity }} className="hero-content relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] text-brand-light mb-6 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-brand shrink-0" /> Chiptuning · Diagnose · Entwicklung
        </motion.p>

        <h1 className="hero-title font-display font-black uppercase tracking-tighter text-white leading-[0.95]">
          <MaskLines lines={["Leistung.", "Neu"]} delay={0.3} />
          <span className="block overflow-hidden">
            <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.66, ease: [0.22,1,0.36,1] }} className="block text-brand">
              definiert.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-7 max-w-lg text-white/75 text-sm sm:text-base leading-relaxed"
        >
          Chiptuning und Steuergeräte-Lösungen direkt am Fahrzeug – für Werkstätten, Tuner
          und ambitionierte Fahrer. Spürbar mehr Leistung und ein besseres Ansprechverhalten,
          alltagstauglich abgestimmt.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <a href="#konfigurator" data-testid="hero-konfigurator-btn"
             className="bg-brand hover:bg-brand-light text-white font-semibold py-4 px-5 sm:px-7 border border-brand-light/40 transition-colors hover:shadow-[0_0_24px_rgba(0,85,255,0.45)] uppercase tracking-wider text-xs sm:text-sm">
            Zum Konfigurator
          </a>
          <a href="#kontakt" data-testid="hero-kontakt-btn"
             className="bg-brand-dark/30 hover:bg-white/5 text-white font-semibold py-4 px-5 sm:px-7 border border-slate-600 transition-colors uppercase tracking-wider text-xs sm:text-sm">
            Anfrage stellen
          </a>
        </motion.div>
      </motion.div>

      <div className="relative z-10 border-t border-white/10 bg-brand-dark/75">
        <div className="hero-stats max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div key={s.label}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 + i * 0.1 }}
              className="py-5 md:py-6 px-3 sm:px-5">
              <div className="font-display font-bold text-xl sm:text-2xl lg:text-3xl text-white">{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-32 right-10 z-10 hidden lg:flex items-center gap-2 text-white/50">
        <ArrowDown size={14} className="animate-bounce" />
        <span className="font-mono text-[10px] uppercase tracking-widest">Scroll</span>
      </motion.div>
    </section>
  );
};
